import { colors, formatters, utilities } from "@fraction/shared";
import { type FunctionComponent, memo, type ReactNode, useCallback, useMemo } from "react";
import RSlider, { type InputSliderProps } from "react-input-slider";
import { RegularText } from "src/components/v1";
import { cn } from "src/utilities/shadcnUtils";

const RSliderComponent = RSlider as unknown as FunctionComponent<InputSliderProps>;

export interface InaccessibleRegion {
  color: string;
  value: number;
  label?: ReactNode;
  labelClassName?: string;
}

export interface SliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (val: number) => void;
  step?: number;
  markers?: Array<number | string>; // string is percentage
  color?: string;
  inaccessibleRegions?: InaccessibleRegion[];
}

export const SLIDER_THUMB_SIZE = 25;

// need just standard object for Slider styles (react-input-slider takes inline CSS objects)
const styles = {
  track: {
    width: "100%",
    backgroundColor: colors.SLIDER_INACTIVE,
    height: 9,
    borderRadius: 0,
  },
  active: {
    backgroundColor: colors.SLIDER_STANDARD,
    height: 9,
    borderRadius: 0,
  },
  thumb: {
    backgroundColor: "white",
    height: SLIDER_THUMB_SIZE,
    width: SLIDER_THUMB_SIZE,
    borderRadius: "50%",
    borderWidth: 1,
    borderColor: colors.SLIDER_STANDARD,
    borderStyle: "solid",
    boxShadow: "2px 2px 8px rgba(0, 0, 0, 0.15)",
    zIndex: 1,
  },
};

const STYLES = {
  marker: "h-5 w-px bg-gray-900 absolute [transform:translateY(-25%)]",
  bottomMarker: "h-5 w-px bg-gray-900",
  markerContainer:
    "absolute top-0 flex flex-col justify-center items-center [transform:translateX(-50%)] w-0",
  markerText: "text-xs text-gray-600 text-center",
  container: "relative z-[1] flex flex-row",
  inaccessibleRegion: "h-[9px]",
};

/**
 * We want a step that can evenly work between min and max so that users can go all the way to the min
 * and all the way to the max.
 *
 * This is a crappy algorithm to try to find such a step. There's probably a more elegant formula.
 */
function findSafeStep(max: number, min: number, startingStep: number) {
  let remainder = (max - min) % startingStep;
  let step = startingStep - remainder;
  remainder = (max - min) % step;

  let tries = 0;

  while (remainder !== 0) {
    if (tries > 10) {
      // give up, just send back a tiny step
      return 1000;
    }
    remainder = (max - min) % step;
    step -= remainder;
    tries++;
  }
  return step;
}

const Slider = ({
  min,
  max,
  value,
  step = 1,
  onChange,
  markers = [],
  inaccessibleRegions = [],
  color = colors.SLIDER_STANDARD,
}: SliderProps) => {
  const handleChange = useCallback(
    ({ x }: { x: number }) => {
      onChange(formatters.number.getNumberFromString(x));
    },
    [onChange]
  );

  const markerLocations = markers.map((marker) => {
    if (typeof marker === "string") {
      return marker;
    }
    return `${utilities.number.boundedPercentage(marker, min, max)}%`;
  });

  const cumulativeInaccessibleWidths = inaccessibleRegions.reduce(
    (sum, region) => sum + utilities.number.boundedPercentage(region.value, min, max),
    0
  );

  const xmin = Math.max(...[min, ...inaccessibleRegions.map((region) => region.value)]);
  const xstep = useMemo(() => findSafeStep(max, xmin, step), [max, step, xmin]);

  return (
    <div className={STYLES.container}>
      {inaccessibleRegions.map((region) => (
        <div
          key={region.value}
          className={STYLES.inaccessibleRegion}
          style={{
            width: `${utilities.number.boundedPercentage(region.value, min, max)}%`,
            backgroundColor: region.color,
          }}
        />
      ))}
      <RSliderComponent
        x={value}
        onChange={handleChange}
        xstep={xstep}
        xmin={xmin}
        xmax={max}
        styles={{
          ...styles,
          track: { ...styles.track, width: `${100 - cumulativeInaccessibleWidths}%` },
          active: { ...styles.active, backgroundColor: color },
          thumb: { ...styles.thumb, borderColor: color },
        }}
      />
      {markerLocations.map((marker, index) => (
        <div className={STYLES.marker} style={{ left: marker }} key={`${marker}-${index}`} />
      ))}
      {inaccessibleRegions
        .filter((region) => region.label)
        .map((region) => (
          <div
            key={region.value}
            className={STYLES.markerContainer}
            style={{ left: `${utilities.number.boundedPercentage(region.value, min, max)}%` }}
          >
            <div className={STYLES.bottomMarker} />
            <RegularText className={cn(STYLES.markerText, region.labelClassName)}>{region.label}</RegularText>
          </div>
        ))}
    </div>
  );
};

export default memo(Slider);
